'use client';

import React, { useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { BidManagement } from './bid-management';
import { BidComparison } from './bid-comparison';
import { ComparisonSheet } from './comparison-sheet';
import { SavedComparisonSheets } from './saved-comparison-sheets';
import { GenericTabBar } from '@/styles/GenericTabBar';
import { BidPackagesPage } from '../bid-packages/BidPackagesPage';
import { useVisibleTabs } from '@/hooks/useVisibleTabs';
import { NothingAvailable } from '@/components/NavigationUnavailable';
import type { NavKey } from '@/lib/navigation-catalog';

const COMPANY_TABS = [
  { value: 'manage', label: 'Overview' },
  { value: 'compare', label: 'AI Comparison' },
  { value: 'sheet', label: 'Comparison Sheet' },
  { value: 'saved', label: 'Saved Sheets' },
  { value: 'packages', label: 'Bid Packages' },
];
// which tabs a role gets is the navigation matrix (nav_visibility), one element per tab -- not a list here
const TAB_KEYS: Record<string, NavKey> = {
  manage: 'cost_management.bids.tab.overview',
  compare: 'cost_management.bids.tab.ai_comparison',
  sheet: 'cost_management.bids.tab.comparison_sheet',
  saved: 'cost_management.bids.tab.saved_sheets',
  packages: 'cost_management.bids.tab.bid_packages',
};

export function BidUploadPage() {
  const [selectedBidsForComparison, setSelectedBidsForComparison] = useState<string[]>([]);
  const visible = useVisibleTabs(COMPANY_TABS, TAB_KEYS);
  const [requestedTab, setRequestedTab] = useState('manage');
  const activeTab = visible.activeOf(requestedTab);
  const setActiveTab = setRequestedTab;
  const [sheetToOpen, setSheetToOpen] = useState<string | null>(null);

  // a drop-box / selection action that targets a tab the role does not have simply leaves the person where they are
  const handleCompareBids = (bidIds: string[]) => {
    if (!visible.has('compare')) return;
    setSelectedBidsForComparison(bidIds);
    setActiveTab('compare');
  };

  const handleOpenSheet = (sheetId: string) => {
    if (!visible.has('sheet')) return;
    setSheetToOpen(sheetId);
    setActiveTab('sheet');
  };

  if (activeTab === undefined) return <NothingAvailable message="No section of Bids is available for your account." />;

  return (
    <GenericTabBar tabs={visible.tabs} activeTab={activeTab} onTabChange={setActiveTab} className="space-y-6">
      {visible.has('manage') && (
        <TabsContent value="manage" className="space-y-6">
          <BidManagement onCompareBids={handleCompareBids} />
        </TabsContent>
      )}

      {visible.has('compare') && (
        <TabsContent value="compare" className="space-y-6">
          <BidComparison preselectedBidIds={selectedBidsForComparison} />
        </TabsContent>
      )}

      {/* forceMount: the sheet editor stays alive (hidden) while another tab is open -- switching tabs never loses its work */}
      {visible.has('sheet') && (
        <TabsContent value="sheet" forceMount className="space-y-6 data-[state=inactive]:hidden">
          <ComparisonSheet openSheetId={sheetToOpen} onSheetOpened={() => setSheetToOpen(null)} />
        </TabsContent>
      )}

      {visible.has('saved') && (
        <TabsContent value="saved" className="space-y-6">
          <SavedComparisonSheets onOpenSheet={handleOpenSheet} />
        </TabsContent>
      )}

      {visible.has('packages') && (
        <TabsContent value="packages" className="space-y-6">
          <BidPackagesPage />
        </TabsContent>
      )}
    </GenericTabBar>
  );
}
